import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as ReturnIcon } from "assets/svgs/navbar/return.svg";
import { ReactComponent as CloseIcon } from "assets/svgs/Close.svg";
import { ReactComponent as DeleteBulkIcon } from "assets/svgs/navbar/delete.svg";
import { ReactComponent as DownloadBulkIcon } from "assets/svgs/navbar/download.svg";
import { ReactComponent as PencilIcon } from "assets/svgs/Editor/pencil.svg";
import { ReactComponent as PlayIcon } from "assets/svgs/navigation/mission/Play.svg";
import OsmLocationMap from "components/map/OsmLocationMap";
import Portal from "components/layout/Portal";
import usePresence from "hooks/usePresence";
import {
    deleteRecordingFile,
    fetchRecordingFileBlob,
    fetchRecordingMeta,
    fetchRecordings,
    getRecordingFileUrl,
    putRecordingMeta,
    type RecordingFileInfo,
    type RecordingGpsMeta,
    type RecordingMetaResponse,
    type RecordingsListResponse,
} from "lib/recorderApi";
import { formatLocalDateTime, LOCAL_DATETIME_MEDIUM_SHORT } from "lib/utils/dateTimeDisplay";
import { useToast } from "providers/ToastProvider";
import { twMerge } from "tailwind-merge";
import {
    formatBytes,
    isPhotoFile,
    isVideoFile,
    preventGalleryContextMenu,
    triggerBrowserDownload,
} from "./recordingsMediaUtils";

/** Hold duration (ms) on a card to enter multi-select mode. */
const LONG_PRESS_MS = 550;

export type GalleryFilter = "all" | "photos" | "videos";

const VideoPreview: React.FC<{ src: string; label: string }> = ({ src, label }) => {
    const ref = useRef<HTMLVideoElement>(null);
    const [broken, setBroken] = useState(false);

    useEffect(() => {
        setBroken(false);
        const el = ref.current;
        if (!el) return;

        const seekPreview = () => {
            try {
                const dur = el.duration;
                if (Number.isFinite(dur) && dur > 0) {
                    el.currentTime = Math.min(0.5, dur * 0.05);
                } else {
                    el.currentTime = 0.1;
                }
            } catch {
                setBroken(true);
            }
        };

        el.addEventListener("loadeddata", seekPreview);
        const onErr = () => setBroken(true);
        el.addEventListener("error", onErr);
        return () => {
            el.removeEventListener("loadeddata", seekPreview);
            el.removeEventListener("error", onErr);
        };
    }, [src]);

    if (broken) {
        return (
            <div className="flex aspect-square w-full items-center justify-center bg-black100 text-h5 text-black60">
                No preview
            </div>
        );
    }

    return (
        <video
            ref={ref}
            src={src}
            muted
            playsInline
            preload="metadata"
            draggable={false}
            className="pointer-events-none aspect-square w-full object-cover"
            aria-label={label}
            onContextMenu={preventGalleryContextMenu}
            style={{ WebkitTouchCallout: "none" }}
        />
    );
};

function mediaKind(name: string): "video" | "photo" | null {
    if (isVideoFile(name)) return "video";
    if (isPhotoFile(name)) return "photo";
    return null;
}

function formatGpsLine(gps: RecordingGpsMeta): string {
    const lat = gps.latitude_deg;
    const lon = gps.longitude_deg;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return "";
    return `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
}

function hasGpsFix(gps: RecordingGpsMeta | null | undefined): gps is RecordingGpsMeta {
    if (!gps) return false;
    return Number.isFinite(gps.latitude_deg) && Number.isFinite(gps.longitude_deg);
}

const SWIPE_CLOSE_PX = 120;
const SWIPE_CLOSE_VELOCITY = 0.55;
const SWIPE_UP_PX = 80;
const SWIPE_UP_VELOCITY = 0.45;
/** Past this drag distance, further motion is rubber-banded (resistance). */
const SWIPE_RUBBER_SOFT_PX = 220;
const SWIPE_RUBBER_FACTOR = 0.28;
/** Spring bounce when releasing without dismiss — overshoots then settles. */
const SWIPE_BOUNCE_TRANSITION =
    "transform 420ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 280ms ease-out";
/** Sheet height snap transition (iOS Photos–style). */
const SHEET_SNAP_TRANSITION =
    "height 320ms cubic-bezier(0.32, 0.72, 0, 1), transform 320ms cubic-bezier(0.32, 0.72, 0, 1)";

const SWIPE_IGNORE_SELECTOR =
    "button,a,input,textarea,select,label,[role='button'],[role='link'],.leaflet-container,[data-sheet-no-swipe]";

/** Photo details bottom sheet: closed → ajar (peek) → expanded (map + actions). */
type PhotoSheetSnap = "closed" | "ajar" | "expanded";

type PhotoSheetFracs = Record<PhotoSheetSnap, number>;

/**
 * Snap heights adapt to viewport: short / landscape phones use a taller
 * expanded sheet so meta + map + actions can fit without clipping.
 */
function computePhotoSheetFracs(vh: number, vw: number): PhotoSheetFracs {
    const short = vh < 560;
    const landscape = vw > vh;
    let expanded = 0.78;
    if (short && landscape) expanded = 0.94;
    else if (short) expanded = 0.9;
    else if (landscape && vh < 700) expanded = 0.86;
    return {
        closed: 0,
        ajar: short ? 0.34 : 0.3,
        expanded,
    };
}

const PHOTO_SHEET_FRAC_FALLBACK = computePhotoSheetFracs(800, 1200);

/** Live sheet snap fractions from viewport size (resize / mobile chrome). */
function usePhotoSheetFracs(): PhotoSheetFracs {
    const [fracs, setFracs] = useState<PhotoSheetFracs>(() =>
        typeof window === "undefined"
            ? PHOTO_SHEET_FRAC_FALLBACK
            : computePhotoSheetFracs(window.innerHeight, window.innerWidth)
    );
    useEffect(() => {
        const update = () => {
            const vh = window.visualViewport?.height ?? window.innerHeight;
            const vw = window.visualViewport?.width ?? window.innerWidth;
            setFracs(computePhotoSheetFracs(vh, vw));
        };
        update();
        window.addEventListener("resize", update);
        window.visualViewport?.addEventListener("resize", update);
        return () => {
            window.removeEventListener("resize", update);
            window.visualViewport?.removeEventListener("resize", update);
        };
    }, []);
    return fracs;
}

/** Side-by-side meta | map when landscape has width, or on large desktops. */
function usePhotoDetailsWideLayout(): boolean {
    const compute = () => {
        if (typeof window === "undefined") return false;
        const w = window.visualViewport?.width ?? window.innerWidth;
        const h = window.visualViewport?.height ?? window.innerHeight;
        const landscape = w > h;
        // Phone/tablet landscape: use horizontal room instead of a tiny stacked map.
        if (landscape && w >= 560) return true;
        // Desktop / large tablet portrait.
        if (w >= 900) return true;
        return false;
    };
    const [wide, setWide] = useState(compute);
    useEffect(() => {
        const onChange = () => setWide(compute());
        onChange();
        window.addEventListener("resize", onChange);
        window.visualViewport?.addEventListener("resize", onChange);
        return () => {
            window.removeEventListener("resize", onChange);
            window.visualViewport?.removeEventListener("resize", onChange);
        };
    }, []);
    return wide;
}

const PHOTO_SHEET_ORDER: PhotoSheetSnap[] = ["closed", "ajar", "expanded"];

function applyRubberBand(dy: number): number {
    const abs = Math.abs(dy);
    const signed = dy < 0 ? -1 : 1;
    if (abs <= SWIPE_RUBBER_SOFT_PX) return dy;
    return signed * (SWIPE_RUBBER_SOFT_PX + (abs - SWIPE_RUBBER_SOFT_PX) * SWIPE_RUBBER_FACTOR);
}

function stepPhotoSheetSnap(
    snap: PhotoSheetSnap,
    direction: "up" | "down"
): PhotoSheetSnap {
    const i = PHOTO_SHEET_ORDER.indexOf(snap);
    if (direction === "up") {
        return PHOTO_SHEET_ORDER[Math.min(PHOTO_SHEET_ORDER.length - 1, i + 1)];
    }
    return PHOTO_SHEET_ORDER[Math.max(0, i - 1)];
}

/**
 * When the details body can scroll, only steal the gesture at scroll edges
 * (same idea as iOS sheets). Handle drags always win.
 */
function canSheetDragFromScroll(
    scrollEl: HTMLElement | null | undefined,
    dy: number,
    fromHandle: boolean
): boolean {
    if (fromHandle || !scrollEl) return true;
    const maxScroll = scrollEl.scrollHeight - scrollEl.clientHeight;
    if (maxScroll <= 1) return true;
    if (dy > 0) {
        // Finger down → collapse sheet only if content is at top.
        return scrollEl.scrollTop <= 0;
    }
    if (dy < 0) {
        // Finger up → expand sheet only if content is at bottom.
        return scrollEl.scrollTop >= maxScroll - 1;
    }
    return true;
}

type VerticalGestureOptions = {
    onSwipeDown?: () => void;
    onSwipeUp?: () => void;
    /** Allow upward drag (for reveal). Default false — down-only dismiss. */
    allowUp?: boolean;
    /**
     * When allowUp: apply upward translateY rubber-band on the target.
     * Default true. Set false to detect swipe-up without moving the fullscreen photo.
     */
    visualUp?: boolean;
    /**
     * Apply downward translateY rubber-band (viewer dismiss chrome).
     * Default true. Set false when collapsing the details sheet from the photo —
     * only fire onSwipeDown; sheet snap animation should match the handle drag.
     */
    visualDown?: boolean;
};

/**
 * Vertical gestures on a fullscreen sheet.
 * Swipe down → onSwipeDown; swipe up (when allowUp) → onSwipeUp.
 */
function useVerticalSheetGestures(
    enabled: boolean,
    {
        onSwipeDown,
        onSwipeUp,
        allowUp = false,
        visualUp = true,
        visualDown = true,
    }: VerticalGestureOptions
) {
    const [offsetY, setOffsetY] = useState(0);
    const [dragging, setDragging] = useState(false);
    const startRef = useRef<{ x: number; y: number; t: number; pointerId: number } | null>(
        null
    );
    const activeRef = useRef(false);
    const captureElRef = useRef<HTMLElement | null>(null);
    const onDownRef = useRef(onSwipeDown);
    const onUpRef = useRef(onSwipeUp);
    onDownRef.current = onSwipeDown;
    onUpRef.current = onSwipeUp;

    const releaseCapture = useCallback(() => {
        const el = captureElRef.current;
        const start = startRef.current;
        if (el && start) {
            try {
                if (el.hasPointerCapture?.(start.pointerId)) {
                    el.releasePointerCapture(start.pointerId);
                }
            } catch {
                /* ignore */
            }
        }
        captureElRef.current = null;
    }, []);

    useEffect(() => {
        if (!enabled) {
            setOffsetY(0);
            setDragging(false);
            startRef.current = null;
            activeRef.current = false;
            captureElRef.current = null;
        }
    }, [enabled]);

    const endGesture = useCallback(
        (clientY: number) => {
            const start = startRef.current;
            const wasActive = activeRef.current;
            releaseCapture();
            startRef.current = null;
            activeRef.current = false;
            if (!start || !wasActive) {
                setDragging(false);
                setOffsetY(0);
                return;
            }
            const dy = clientY - start.y;
            const dt = Math.max(1, Date.now() - start.t);
            const velocity = dy / dt;
            if (dy >= SWIPE_CLOSE_PX || velocity >= SWIPE_CLOSE_VELOCITY) {
                setDragging(false);
                // Do not spring offsetY back to 0 before dismiss — that fights
                // animate-ios-sheet-out (snap-up then slide-down = wobble).
                onDownRef.current?.();
                setOffsetY(0);
                return;
            }
            if (
                allowUp &&
                (dy <= -SWIPE_UP_PX || velocity <= -SWIPE_UP_VELOCITY)
            ) {
                setDragging(false);
                setOffsetY(0);
                onUpRef.current?.();
                return;
            }
            setDragging(false);
            requestAnimationFrame(() => {
                setOffsetY(0);
            });
        },
        [allowUp, releaseCapture]
    );

    const bindSwipeTarget = useMemo(() => {
        if (!enabled) return {};
        return {
            onPointerDown: (e: React.PointerEvent) => {
                if (e.button !== 0) return;
                const target = e.target;
                if (
                    target instanceof Element &&
                    target.closest(SWIPE_IGNORE_SELECTOR)
                ) {
                    return;
                }
                startRef.current = {
                    x: e.clientX,
                    y: e.clientY,
                    t: Date.now(),
                    pointerId: e.pointerId,
                };
                activeRef.current = false;
            },
            onPointerMove: (e: React.PointerEvent) => {
                if (!startRef.current) return;
                const dy = e.clientY - startRef.current.y;
                const dx = e.clientX - startRef.current.x;
                if (!activeRef.current) {
                    if (Math.abs(dy) < 10 && Math.abs(dx) < 10) return;
                    if (Math.abs(dy) <= Math.abs(dx)) {
                        startRef.current = null;
                        return;
                    }
                    if (!allowUp && dy <= 0) {
                        startRef.current = null;
                        return;
                    }
                    activeRef.current = true;
                    const el = e.currentTarget as HTMLElement;
                    captureElRef.current = el;
                    try {
                        el.setPointerCapture(e.pointerId);
                    } catch {
                        /* ignore */
                    }
                }
                // Clamp visual feedback independently of gesture detection.
                let visualDy = dy;
                if (!visualUp) visualDy = Math.max(0, visualDy);
                if (!visualDown) visualDy = Math.min(0, visualDy);
                const next = applyRubberBand(visualDy);
                setOffsetY(next);
                if ((next > 0 && visualDown) || (next < 0 && visualUp)) {
                    setDragging(true);
                }
            },
            onPointerUp: (e: React.PointerEvent) => {
                endGesture(e.clientY);
            },
            onPointerCancel: (e: React.PointerEvent) => {
                endGesture(e.clientY);
            },
        };
    }, [enabled, endGesture, allowUp, visualUp, visualDown]);

    // Dismiss chrome only when the photo actually moves (visualDown).
    // Sheet collapse/expand from the photo must not translate the viewer —
    // that re-triggers animate-ios-sheet-in and fights the sheet snap animation.
    const sheetStyle: React.CSSProperties =
        offsetY > 0 && visualDown
            ? {
                  transform: `translateY(${offsetY}px)`,
                  opacity: Math.max(0.5, 1 - offsetY / 480),
                  transition: dragging ? "none" : SWIPE_BOUNCE_TRANSITION,
                  touchAction: "pan-y",
              }
            : visualUp && offsetY < 0
              ? {
                    transform: `translateY(${offsetY}px)`,
                    opacity: Math.max(0.85, 1 + offsetY / 600),
                    transition: dragging ? "none" : SWIPE_BOUNCE_TRANSITION,
                    touchAction: "pan-y",
                }
              : { touchAction: "pan-y" };

    return { bindSwipeTarget, sheetStyle, offsetY, dragging };
}

/**
 * Drag the photo details sheet between closed / ajar / expanded snap heights.
 * dy > 0 = drag down (collapse); dy < 0 = drag up (expand).
 * Bind on the whole sheet; map / controls / mid-scroll content are ignored.
 */
function usePhotoSheetSnapGestures(
    enabled: boolean,
    snap: PhotoSheetSnap,
    setSnap: (next: PhotoSheetSnap) => void,
    fracs: PhotoSheetFracs,
    scrollRef?: React.RefObject<HTMLElement | null>,
    /** Fired after an activated drag settles — use to ignore ghost clicks that reopen the sheet. */
    onDragCommitted?: () => void
) {
    const [dragFrac, setDragFrac] = useState<number | null>(null);
    const [dragging, setDragging] = useState(false);
    const startRef = useRef<{
        x: number;
        y: number;
        t: number;
        pointerId: number;
        startFrac: number;
        fromHandle: boolean;
    } | null>(null);
    const lastYRef = useRef(0);
    const activeRef = useRef(false);
    const captureElRef = useRef<HTMLElement | null>(null);
    /** After a real drag, ignore the synthetic click (Details button would reopen ajar). */
    const suppressClickRef = useRef(false);
    const snapRef = useRef(snap);
    snapRef.current = snap;
    const fracsRef = useRef(fracs);
    fracsRef.current = fracs;

    const releaseCapture = useCallback(() => {
        const el = captureElRef.current;
        const start = startRef.current;
        if (el && start) {
            try {
                if (el.hasPointerCapture?.(start.pointerId)) {
                    el.releasePointerCapture(start.pointerId);
                }
            } catch {
                /* ignore */
            }
        }
        captureElRef.current = null;
    }, []);

    useEffect(() => {
        if (!enabled) {
            setDragFrac(null);
            setDragging(false);
            startRef.current = null;
            activeRef.current = false;
            captureElRef.current = null;
            suppressClickRef.current = false;
        }
    }, [enabled]);

    useEffect(() => {
        if (!dragging) setDragFrac(null);
    }, [snap, dragging]);

    const endGesture = useCallback(() => {
        const start = startRef.current;
        const wasActive = activeRef.current;
        const lastY = lastYRef.current;
        releaseCapture();
        startRef.current = null;
        activeRef.current = false;
        if (!start || !wasActive) {
            setDragging(false);
            setDragFrac(null);
            return;
        }
        // Swallow the click that browsers fire after pointerup on the same target.
        suppressClickRef.current = true;
        const dy = lastY - start.y;
        const startSnap = snapRef.current;
        // Same rule as swiping on the photo: one snap step in the drag direction.
        const nextSnap =
            dy > 0
                ? stepPhotoSheetSnap(startSnap, "down")
                : dy < 0
                  ? stepPhotoSheetSnap(startSnap, "up")
                  : startSnap;
        setDragging(false);
        setDragFrac(null);
        if (nextSnap !== startSnap) {
            setSnap(nextSnap);
        }
        // Collapsing mounts the closed "Details" peek under the finger; block that click.
        onDragCommitted?.();
    }, [releaseCapture, setSnap, onDragCommitted]);

    const bindSheetDrag = useMemo(() => {
        if (!enabled) return {};
        return {
            onPointerDown: (e: React.PointerEvent) => {
                if (e.button !== 0) return;
                const target = e.target;
                if (!(target instanceof Element)) return;
                // Map pan/zoom and Edit/Download/Delete own the gesture.
                // Details handle (incl. its label button) still participates.
                if (
                    target.closest(SWIPE_IGNORE_SELECTOR) &&
                    !target.closest("[data-sheet-handle]")
                ) {
                    return;
                }
                suppressClickRef.current = false;
                const fromHandle = Boolean(target.closest("[data-sheet-handle]"));
                startRef.current = {
                    x: e.clientX,
                    y: e.clientY,
                    t: Date.now(),
                    pointerId: e.pointerId,
                    startFrac: fracsRef.current[snapRef.current],
                    fromHandle,
                };
                lastYRef.current = e.clientY;
                activeRef.current = false;
            },
            onPointerMove: (e: React.PointerEvent) => {
                if (!startRef.current) return;
                lastYRef.current = e.clientY;
                const dy = e.clientY - startRef.current.y;
                const dx = e.clientX - startRef.current.x;
                if (!activeRef.current) {
                    if (Math.abs(dy) < 10 && Math.abs(dx) < 10) return;
                    if (Math.abs(dy) <= Math.abs(dx)) {
                        startRef.current = null;
                        return;
                    }
                    if (
                        !canSheetDragFromScroll(
                            scrollRef?.current,
                            dy,
                            startRef.current.fromHandle
                        )
                    ) {
                        startRef.current = null;
                        return;
                    }
                    activeRef.current = true;
                    setDragging(true);
                    const el = e.currentTarget as HTMLElement;
                    captureElRef.current = el;
                    try {
                        el.setPointerCapture(e.pointerId);
                    } catch {
                        /* ignore */
                    }
                }
                e.preventDefault();
                const vh = Math.max(1, window.innerHeight);
                const expanded = fracsRef.current.expanded;
                const raw = startRef.current.startFrac - dy / vh;
                const clamped = Math.max(-0.04, Math.min(expanded + 0.06, raw));
                let next = clamped;
                if (clamped < 0) next = applyRubberBand(clamped * vh) / vh;
                if (clamped > expanded) {
                    const over = (clamped - expanded) * vh;
                    next = expanded + applyRubberBand(over) / vh;
                }
                setDragFrac(next);
            },
            onPointerUp: () => {
                endGesture();
            },
            onPointerCancel: () => {
                endGesture();
            },
            onLostPointerCapture: () => {
                if (activeRef.current) endGesture();
            },
            onClickCapture: (e: React.MouseEvent) => {
                if (!suppressClickRef.current) return;
                suppressClickRef.current = false;
                e.preventDefault();
                e.stopPropagation();
            },
        };
    }, [enabled, endGesture, scrollRef]);

    const heightFrac = dragFrac ?? fracs[snap];
    const sheetStyle: React.CSSProperties = {
        height: `${Math.max(0, heightFrac) * 100}dvh`,
        transition: dragging ? "none" : SHEET_SNAP_TRANSITION,
        // Sheet chrome uses none; the scroll body sets pan-y so overflow can scroll.
        touchAction: "none",
    };

    return { bindSheetDrag, sheetStyle, heightFrac, dragging };
}

/** @deprecated Prefer useVerticalSheetGestures — kept as thin alias for video sheet. */
function useSwipeDownDismiss(onClose: () => void, enabled: boolean) {
    return useVerticalSheetGestures(enabled, {
        onSwipeDown: onClose,
        allowUp: false,
        visualDown: false,
    });
}

interface RecordingsGalleryProps {
    baseUrl: string | null;
    className?: string;
}

const RecordingsGallery: React.FC<RecordingsGalleryProps> = ({ baseUrl, className }) => {
    const { t } = useTranslation("control");
    const { addToast } = useToast();
    const [filter, setFilter] = useState<GalleryFilter>("all");
    const [data, setData] = useState<RecordingsListResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [listError, setListError] = useState<string | null>(null);
    const [playingName, setPlayingName] = useState<string | null>(null);
    const [viewingPhotoName, setViewingPhotoName] = useState<string | null>(null);
    /** Fullscreen photo; details sheet: closed → ajar (peek) → expanded (map). */
    const [photoSheetSnap, setPhotoSheetSnap] = useState<PhotoSheetSnap>("closed");
    const [viewingMeta, setViewingMeta] = useState<RecordingMetaResponse | null>(null);
    const [viewingMetaReady, setViewingMetaReady] = useState(false);
    const [playingMeta, setPlayingMeta] = useState<RecordingMetaResponse | null>(null);
    const [playingMetaReady, setPlayingMetaReady] = useState(false);
    const [bulkSelectActive, setBulkSelectActive] = useState(false);
    const [bulkSelected, setBulkSelected] = useState<Set<string>>(() => new Set());
    const [batchDeleting, setBatchDeleting] = useState(false);
    const [batchDownloading, setBatchDownloading] = useState(false);
    const longPressTimerRef = useRef<number | null>(null);
    const longPressOriginRef = useRef<{ x: number; y: number } | null>(null);
    const longPressTriggeredRef = useRef(false);
    const selectAllCheckboxRef = useRef<HTMLInputElement>(null);
    /** Suppress photo tap when a vertical swipe already fired. */
    const photoSwipeFiredRef = useRef(false);
    const photoTapStartRef = useRef<{ x: number; y: number } | null>(null);
    const photoSheetScrollRef = useRef<HTMLDivElement | null>(null);
    const photoSheetFracs = usePhotoSheetFracs();
    const photoDetailsWide = usePhotoDetailsWideLayout();
    const [mediaEdit, setMediaEdit] = useState<{
        filename: string;
        kind: "video" | "photo";
    } | null>(null);
    const [editName, setEditName] = useState("");
    const [editRemark, setEditRemark] = useState("");
    const [editGps, setEditGps] = useState<RecordingGpsMeta | null>(null);
    const [editTargetFile, setEditTargetFile] = useState("");
    const [editSaving, setEditSaving] = useState(false);
    const metaFetchGenRef = useRef(0);
    const detailMetaGenRef = useRef(0);

    const clearLongPressTimer = useCallback(() => {
        if (longPressTimerRef.current) {
            window.clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
        longPressOriginRef.current = null;
    }, []);

    const exitBulkSelection = useCallback(() => {
        setBulkSelectActive(false);
        setBulkSelected(new Set());
        clearLongPressTimer();
        longPressTriggeredRef.current = false;
    }, [clearLongPressTimer]);

    const loadList = useCallback(async () => {
        if (!baseUrl) {
            setListError(
                "Recorder URL is not configured. Set REACT_APP_RECORDER_BASE_URL or ARC host / REACT_APP_RECORDER_HOST."
            );
            setData(null);
            return;
        }
        setLoading(true);
        setListError(null);
        try {
            const res = await fetchRecordings(baseUrl);
            setData(res);
        } catch (e) {
            setData(null);
            setListError(e instanceof Error ? e.message : "Failed to load recordings");
        } finally {
            setLoading(false);
        }
    }, [baseUrl]);

    useEffect(() => {
        void loadList();
    }, [loadList]);

    useEffect(() => {
        exitBulkSelection();
    }, [filter, exitBulkSelection]);

    const filteredFiles = useMemo(() => {
        if (!data) return [];
        return data.files.filter((f) => {
            const kind = mediaKind(f.name);
            if (!kind) return false;
            if (filter === "all") return true;
            if (filter === "photos") return kind === "photo";
            return kind === "video";
        });
    }, [data, filter]);

    const fileByName = useMemo(() => {
        const map = new Map<string, RecordingFileInfo>();
        for (const f of data?.files ?? []) map.set(f.name, f);
        return map;
    }, [data]);

    const bulkContextNames = useMemo(
        () => filteredFiles.map((f) => f.name),
        [filteredFiles]
    );

    const bulkAllSelected =
        bulkContextNames.length > 0 &&
        bulkContextNames.every((n) => bulkSelected.has(n));

    const bulkSelectedCount = useMemo(
        () => bulkContextNames.filter((n) => bulkSelected.has(n)).length,
        [bulkContextNames, bulkSelected]
    );

    useLayoutEffect(() => {
        const el = selectAllCheckboxRef.current;
        if (!el || !bulkSelectActive) return;
        const n = bulkContextNames.length;
        const c = bulkContextNames.filter((nm) => bulkSelected.has(nm)).length;
        el.indeterminate = n > 0 && c > 0 && c < n;
    }, [bulkSelectActive, bulkContextNames, bulkSelected]);

    const toggleBulkOne = useCallback((name: string) => {
        setBulkSelected((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        if (bulkAllSelected) {
            setBulkSelected((prev) => {
                const next = new Set(prev);
                for (const n of bulkContextNames) next.delete(n);
                return next;
            });
        } else {
            setBulkSelected((prev) => {
                const next = new Set(prev);
                for (const n of bulkContextNames) next.add(n);
                return next;
            });
        }
    }, [bulkAllSelected, bulkContextNames]);

    const onCardTouchStart = useCallback(
        (name: string) => (e: React.TouchEvent) => {
            if (e.touches.length !== 1) return;
            clearLongPressTimer();
            longPressTriggeredRef.current = false;
            const touch = e.touches[0];
            longPressOriginRef.current = { x: touch.clientX, y: touch.clientY };
            longPressTimerRef.current = window.setTimeout(() => {
                longPressTimerRef.current = null;
                longPressOriginRef.current = null;
                longPressTriggeredRef.current = true;
                setBulkSelectActive(true);
                setBulkSelected((prev) => new Set(prev).add(name));
            }, LONG_PRESS_MS);
        },
        [clearLongPressTimer]
    );

    const onCardTouchMove = useCallback(
        (e: React.TouchEvent) => {
            const origin = longPressOriginRef.current;
            if (!origin || longPressTimerRef.current == null) return;
            const touch = e.touches[0];
            if (!touch) return;
            const dx = touch.clientX - origin.x;
            const dy = touch.clientY - origin.y;
            if (dx * dx + dy * dy > 100) {
                clearLongPressTimer();
            }
        },
        [clearLongPressTimer]
    );

    const onCardTouchEnd = useCallback(() => {
        clearLongPressTimer();
    }, [clearLongPressTimer]);

    const fetchDetailMeta = useCallback(
        (name: string, kind: "photo" | "video") => {
            if (!baseUrl) return;
            const gen = ++detailMetaGenRef.current;
            if (kind === "photo") {
                setViewingMeta(null);
                setViewingMetaReady(false);
            } else {
                setPlayingMeta(null);
                setPlayingMetaReady(false);
            }
            void fetchRecordingMeta(baseUrl, name)
                .then((m) => {
                    if (detailMetaGenRef.current !== gen) return;
                    if (kind === "photo") {
                        setViewingMeta(m);
                        setViewingMetaReady(true);
                    } else {
                        setPlayingMeta(m);
                        setPlayingMetaReady(true);
                    }
                })
                .catch(() => {
                    if (detailMetaGenRef.current !== gen) return;
                    if (kind === "photo") {
                        setViewingMeta(null);
                        setViewingMetaReady(true);
                    } else {
                        setPlayingMeta(null);
                        setPlayingMetaReady(true);
                    }
                });
        },
        [baseUrl]
    );

    const openPhotoDetail = useCallback(
        (name: string) => {
            setPhotoSheetSnap("closed");
            setViewingPhotoName(name);
            fetchDetailMeta(name, "photo");
        },
        [fetchDetailMeta]
    );

    const openVideoDetail = useCallback(
        (name: string) => {
            setPlayingName(name);
            fetchDetailMeta(name, "video");
        },
        [fetchDetailMeta]
    );

    const onCardClick = useCallback(
        (name: string, kind: "photo" | "video") => {
            // Swallow the synthetic click that follows a successful long-press.
            if (longPressTriggeredRef.current) {
                longPressTriggeredRef.current = false;
                return;
            }
            if (bulkSelectActive) {
                toggleBulkOne(name);
                return;
            }
            if (kind === "video") openVideoDetail(name);
            else openPhotoDetail(name);
        },
        [bulkSelectActive, openPhotoDetail, openVideoDetail, toggleBulkOne]
    );

    const closePhotoDetail = useCallback(() => {
        setPhotoSheetSnap("closed");
        setViewingPhotoName(null);
    }, []);

    const closeVideoDetail = useCallback(() => {
        setPlayingName(null);
    }, []);

    const collapsePhotoSheetOne = useCallback(() => {
        setPhotoSheetSnap((prev) => stepPhotoSheetSnap(prev, "down"));
    }, []);

    const photoOpen = Boolean(viewingPhotoName && baseUrl);
    const videoOpen = Boolean(playingName && baseUrl);
    const { mounted: photoMounted, state: photoPresence } = usePresence(photoOpen);
    const { mounted: videoMounted, state: videoPresence } = usePresence(videoOpen);

    const retainedPhotoNameRef = useRef<string | null>(null);
    const retainedVideoNameRef = useRef<string | null>(null);
    if (viewingPhotoName) retainedPhotoNameRef.current = viewingPhotoName;
    if (playingName) retainedVideoNameRef.current = playingName;
    const displayPhotoName = viewingPhotoName ?? retainedPhotoNameRef.current;
    const displayVideoName = playingName ?? retainedVideoNameRef.current;

    useEffect(() => {
        if (!photoMounted) {
            setViewingMeta(null);
            setViewingMetaReady(false);
            retainedPhotoNameRef.current = null;
            setPhotoSheetSnap("closed");
        }
    }, [photoMounted]);

    useEffect(() => {
        if (!videoMounted) {
            setPlayingMeta(null);
            setPlayingMetaReady(false);
            retainedVideoNameRef.current = null;
        }
    }, [videoMounted]);

    useEffect(() => {
        if (!photoOpen) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            e.stopPropagation();
            if (photoSheetSnap !== "closed") {
                setPhotoSheetSnap((prev) => stepPhotoSheetSnap(prev, "down"));
            } else {
                closePhotoDetail();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [photoOpen, photoSheetSnap, closePhotoDetail]);

    /** After sheet drag collapse, ignore ghost clicks that would reopen ajar. */
    const blockSheetOpenUntilRef = useRef(0);
    const onSheetDragCommitted = useCallback(() => {
        blockSheetOpenUntilRef.current = Date.now() + 500;
    }, []);

    const advancePhotoDetails = useCallback(() => {
        if (Date.now() < blockSheetOpenUntilRef.current) return;
        // Same stepping as the Details edge-handle button.
        setPhotoSheetSnap((prev) => {
            if (prev === "closed") return "ajar";
            if (prev === "ajar") return "expanded";
            return "ajar";
        });
    }, []);

    // Delay the closed peek cue so it cannot receive the leftover click after collapse.
    const [closedPeekReady, setClosedPeekReady] = useState(false);
    useEffect(() => {
        if (photoSheetSnap !== "closed") {
            setClosedPeekReady(false);
            return;
        }
        const id = window.setTimeout(() => setClosedPeekReady(true), 400);
        return () => window.clearTimeout(id);
    }, [photoSheetSnap]);

    // No live translateY on the photo viewer — rubber-band + ios-sheet-out fought
    // each other (bounce back then slide out = wobble). Swipe only commits the
    // action; close uses the same animate-ios-sheet-out as the X button.
    const photoGestures = useVerticalSheetGestures(photoOpen, {
        onSwipeDown: () => {
            photoSwipeFiredRef.current = true;
            if (photoSheetSnap !== "closed") {
                blockSheetOpenUntilRef.current = Date.now() + 500;
                collapsePhotoSheetOne();
            } else closePhotoDetail();
        },
        onSwipeUp: () => {
            photoSwipeFiredRef.current = true;
            advancePhotoDetails();
        },
        allowUp: true,
        visualUp: false,
        visualDown: false,
    });
    const photoSheetGestures = usePhotoSheetSnapGestures(
        photoOpen,
        photoSheetSnap,
        setPhotoSheetSnap,
        photoSheetFracs,
        photoSheetScrollRef,
        onSheetDragCommitted
    );
    const videoSwipe = useSwipeDownDismiss(closeVideoDetail, videoOpen);

    const onPhotoSurfaceClick = useCallback(() => {
        if (Date.now() < blockSheetOpenUntilRef.current) return;
        // Tap photo: closed → ajar (iOS “show info”); ajar/expanded → tuck sheet closed.
        if (photoSheetSnap === "closed") advancePhotoDetails();
        else setPhotoSheetSnap("closed");
    }, [photoSheetSnap, advancePhotoDetails]);

    const handleBatchDelete = async () => {
        if (!baseUrl || bulkSelectedCount === 0) return;
        const names = bulkContextNames.filter((n) => bulkSelected.has(n));
        if (!window.confirm(`Delete ${names.length} file(s)? This cannot be undone.`)) {
            return;
        }
        setBatchDeleting(true);
        let failed = 0;
        try {
            for (const name of names) {
                try {
                    await deleteRecordingFile(baseUrl, name);
                    if (playingName === name) closeVideoDetail();
                    if (viewingPhotoName === name) closePhotoDetail();
                    if (mediaEdit && editTargetFile === name) setMediaEdit(null);
                } catch {
                    failed += 1;
                }
            }
            if (failed > 0) {
                addToast({
                    type: "error",
                    message: `${failed} file(s) could not be deleted.`,
                });
            } else {
                addToast({ type: "success", message: "Files deleted." });
            }
            await loadList();
            exitBulkSelection();
        } finally {
            setBatchDeleting(false);
        }
    };

    const handleDeleteOne = async (name: string) => {
        if (!baseUrl || !name) return;
        if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) {
            return;
        }
        setBatchDeleting(true);
        try {
            await deleteRecordingFile(baseUrl, name);
            if (playingName === name) closeVideoDetail();
            if (viewingPhotoName === name) closePhotoDetail();
            if (mediaEdit && editTargetFile === name) setMediaEdit(null);
            addToast({ type: "success", message: "File deleted." });
            await loadList();
        } catch (e) {
            addToast({
                type: "error",
                message: e instanceof Error ? e.message : "Delete failed.",
            });
        } finally {
            setBatchDeleting(false);
        }
    };

    const handleDownloadOne = async (name: string) => {
        if (!baseUrl || !name) return;
        try {
            const blob = await fetchRecordingFileBlob(baseUrl, name);
            triggerBrowserDownload(blob, name);
            addToast({ type: "success", message: "Download started." });
        } catch (e) {
            addToast({
                type: "error",
                message: e instanceof Error ? e.message : "Download failed.",
            });
        }
    };

    const handleBatchDownload = async () => {
        if (!baseUrl || bulkSelectedCount === 0) return;
        const names = bulkContextNames.filter((n) => bulkSelected.has(n));
        setBatchDownloading(true);
        let failed = 0;
        try {
            for (let i = 0; i < names.length; i++) {
                const name = names[i];
                try {
                    const blob = await fetchRecordingFileBlob(baseUrl, name);
                    triggerBrowserDownload(blob, name);
                    if (i < names.length - 1) {
                        await new Promise((r) => window.setTimeout(r, 250));
                    }
                } catch {
                    failed += 1;
                }
            }
            if (failed > 0) {
                addToast({
                    type: "error",
                    message: `${failed} file(s) could not be downloaded.`,
                });
            } else {
                addToast({
                    type: "success",
                    message:
                        names.length === 1
                            ? "Download started."
                            : `Started download of ${names.length} files.`,
                });
            }
        } finally {
            setBatchDownloading(false);
        }
    };

    const openMediaEdit = useCallback(
        (f: RecordingFileInfo, kind: "video" | "photo") => {
            const gen = ++metaFetchGenRef.current;
            setMediaEdit({ filename: f.name, kind });
            setEditName(f.name);
            setEditRemark(f.remark ?? "");
            setEditGps(null);
            setEditTargetFile(f.name);
            if (baseUrl) {
                void fetchRecordingMeta(baseUrl, f.name)
                    .then((m) => {
                        if (metaFetchGenRef.current !== gen) return;
                        setEditRemark(m.remark);
                        setEditGps(m.gps ?? null);
                    })
                    .catch(() => {});
            }
        },
        [baseUrl]
    );

    const closeMediaEdit = () => {
        setMediaEdit(null);
        setEditGps(null);
    };

    const saveMediaEdit = async () => {
        if (!baseUrl || !mediaEdit) return;
        const trimmed = editName.trim();
        if (!trimmed) {
            addToast({ type: "error", message: "File name cannot be empty." });
            return;
        }
        const origExt = editTargetFile.match(/\.[^.]+$/)?.[0]?.toLowerCase() ?? "";
        const nextExt = trimmed.match(/\.[^.]+$/)?.[0]?.toLowerCase() ?? "";
        if (origExt !== nextExt) {
            addToast({
                type: "error",
                message: `Extension must stay ${origExt || "unchanged"}.`,
            });
            return;
        }
        if (/[/\\]/.test(trimmed) || trimmed.includes("..")) {
            addToast({ type: "error", message: "Invalid file name." });
            return;
        }
        setEditSaving(true);
        try {
            const r = await putRecordingMeta(baseUrl, editTargetFile, {
                remark: editRemark,
                new_filename: trimmed !== editTargetFile ? trimmed : undefined,
            });
            const newFn = r.filename;
            if (playingName === editTargetFile) {
                setPlayingName(newFn);
                fetchDetailMeta(newFn, "video");
            }
            if (viewingPhotoName === editTargetFile) {
                setViewingPhotoName(newFn);
                fetchDetailMeta(newFn, "photo");
            }
            addToast({ type: "success", message: "Saved." });
            await loadList();
            closeMediaEdit();
        } catch (e) {
            addToast({
                type: "error",
                message: e instanceof Error ? e.message : "Save failed",
            });
        } finally {
            setEditSaving(false);
        }
    };

    const bulkActionsPending = batchDeleting || batchDownloading;

    const filterChipClass = (active: boolean) =>
        twMerge(
            "rounded-r5 border px-3 py-1.5 text-h5 transition-colors",
            active
                ? "border-primary2/60 bg-primary2/15 text-primary2"
                : "border-black80 bg-black80 text-black40 hover:border-black60 hover:text-white"
        );

    const emptyMessage =
        filter === "photos"
            ? t("gallery.emptyPhotos", { defaultValue: "No photos yet." })
            : filter === "videos"
              ? t("gallery.emptyVideos", { defaultValue: "No videos yet." })
              : t("gallery.emptyAll", { defaultValue: "No photos or videos yet." });

    const viewingFile = displayPhotoName
        ? fileByName.get(displayPhotoName)
        : undefined;
    const playingFile = displayVideoName
        ? fileByName.get(displayVideoName)
        : undefined;

    const renderMediaActions = (
        name: string,
        file: RecordingFileInfo | undefined,
        kind: "photo" | "video",
        options?: { className?: string }
    ) => (
        <div
            className={twMerge(
                "flex flex-wrap items-center gap-2",
                options?.className
            )}
        >
            {file ? (
                <button
                    type="button"
                    className="inline-flex min-h-[2.75rem] items-center gap-1.5 rounded-r5 border border-primary2 bg-primary2 px-3 py-1.5 text-h5 font-semibold text-white hover:bg-primary1 disabled:opacity-50"
                    disabled={bulkActionsPending}
                    onClick={() => openMediaEdit(file, kind)}
                >
                    <PencilIcon
                        className="h-4 w-4 shrink-0 [&_path]:fill-current"
                        style={{ width: "1rem", height: "1rem" }}
                        aria-hidden
                    />
                    {t("gallery.edit", { defaultValue: "Edit" })}
                </button>
            ) : null}
            <button
                type="button"
                className="inline-flex min-h-[2.75rem] items-center gap-1.5 rounded-r5 border border-black80 bg-black80 px-3 py-1.5 text-h5 text-white hover:bg-black70 disabled:opacity-50"
                disabled={bulkActionsPending}
                onClick={() => void handleDownloadOne(name)}
            >
                <DownloadBulkIcon
                    className="h-4 w-4 shrink-0"
                    style={{ width: "1rem", height: "1rem" }}
                    aria-hidden
                />
                {t("gallery.download", { defaultValue: "Download" })}
            </button>
            <button
                type="button"
                className="inline-flex min-h-[2.75rem] items-center gap-1.5 rounded-r5 border border-red-900/50 bg-red-950/40 px-3 py-1.5 text-h5 text-red-300 hover:bg-red-950/55 disabled:opacity-50"
                disabled={bulkActionsPending}
                onClick={() => void handleDeleteOne(name)}
            >
                <DeleteBulkIcon
                    className="h-4 w-4 shrink-0"
                    style={{ width: "1rem", height: "1rem" }}
                    aria-hidden
                />
                {t("gallery.delete", { defaultValue: "Delete" })}
            </button>
        </div>
    );

    const renderDetailInfo = (
        name: string,
        file: RecordingFileInfo | undefined,
        meta: RecordingMetaResponse | null,
        metaReady: boolean,
        kind: "photo" | "video",
        options?: { className?: string; mapClassName?: string; showMap?: boolean }
    ) => {
        const remark = meta?.remark?.trim() || file?.remark?.trim() || "";
        const gps = meta?.gps;
        const includeMap = options?.showMap !== false;
        const showMap = includeMap && kind === "photo" && hasGpsFix(gps);
        const showNoGps = includeMap && kind === "photo" && metaReady && !hasGpsFix(gps);

        return (
            <div
                className={twMerge(
                    "flex flex-col gap-3 bg-black90 px-4 py-4",
                    options?.className
                )}
            >
                <div>
                    <p className="text-h5 font-semibold break-all text-white">{name}</p>
                    <p className="mt-1 text-h5 text-black40">
                        {file
                            ? `${formatBytes(file.size_bytes)} · ${formatLocalDateTime(
                                  file.modified_utc,
                                  LOCAL_DATETIME_MEDIUM_SHORT
                              )}`
                            : "\u00a0"}
                    </p>
                    {remark ? (
                        <p className="mt-2 text-h5 text-black40">{remark}</p>
                    ) : null}
                </div>

                {showMap && gps ? (
                    <div className="shrink-0 space-y-2">
                        <div>
                            <p className="mb-1 text-h5 font-semibold text-white">
                                {t("gallery.location", { defaultValue: "Location" })}
                            </p>
                            <p className="text-h5 text-black40">{formatGpsLine(gps)}</p>
                        </div>
                        <OsmLocationMap
                            latitude={gps.latitude_deg}
                            longitude={gps.longitude_deg}
                            interactive
                            className={twMerge(
                                "mx-auto aspect-square h-auto w-full max-w-[min(100%,24rem)] shrink-0",
                                options?.mapClassName
                            )}
                        />
                    </div>
                ) : showNoGps ? (
                    <div>
                        <p className="mb-1 text-h5 font-semibold text-white">
                            {t("gallery.location", { defaultValue: "Location" })}
                        </p>
                        <p className="text-h5 text-black40">
                            {t("gallery.locationUnavailable", {
                                defaultValue: "No GPS location for this photo.",
                            })}
                        </p>
                    </div>
                ) : null}

                <div className="mt-auto pt-1">{renderMediaActions(name, file, kind)}</div>
            </div>
        );
    };

    const photoGps = viewingMeta?.gps;
    const photoHasGps = hasGpsFix(photoGps);
    const photoShowNoGps = viewingMetaReady && !photoHasGps;
    const photoRemark =
        viewingMeta?.remark?.trim() || viewingFile?.remark?.trim() || "";
    const showPhotoExpandedBody =
        photoSheetSnap === "expanded" ||
        photoSheetGestures.heightFrac > photoSheetFracs.ajar + 0.12;

    const photoMetaBlock = (
        <div className="shrink-0 text-center">
            <p className="truncate text-h5 font-semibold text-white">
                {displayPhotoName}
            </p>
            <p className="mt-1 text-h5 text-black40">
                {viewingFile
                    ? `${formatBytes(viewingFile.size_bytes)} · ${formatLocalDateTime(
                          viewingFile.modified_utc,
                          LOCAL_DATETIME_MEDIUM_SHORT
                      )}`
                    : "\u00a0"}
            </p>
            {photoHasGps && photoGps ? (
                <p className="mt-1 text-h5 text-black40">{formatGpsLine(photoGps)}</p>
            ) : null}
            {photoRemark ? (
                <p className="mt-2 line-clamp-3 text-h5 text-black40">{photoRemark}</p>
            ) : null}
        </div>
    );

    const photoMapBlock =
        photoHasGps && photoGps ? (
            <div className="flex w-full shrink-0 flex-col items-center gap-2">
                <p className="shrink-0 text-center text-h5 font-semibold text-white">
                    {t("gallery.location", { defaultValue: "Location" })}
                </p>
                <div className="flex w-full items-center justify-center">
                    <div
                        data-sheet-no-swipe
                        className={twMerge(
                            "relative mx-auto aspect-square overflow-hidden rounded-r10 border border-black80 bg-black100",
                            // Portrait phone: compact square that fits with sticky actions.
                            "h-[min(36dvh,18rem)] w-[min(100%,36dvh,18rem)]",
                            // Landscape / browser: larger centered map.
                            "landscape:h-[min(58dvh,26rem)] landscape:w-[min(100%,58dvh,26rem)]",
                            photoDetailsWide &&
                                "h-[min(62dvh,32rem)] w-[min(100%,62dvh,32rem)] landscape:h-[min(62dvh,32rem)] landscape:w-[min(100%,62dvh,32rem)]"
                        )}
                    >
                        <OsmLocationMap
                            latitude={photoGps.latitude_deg}
                            longitude={photoGps.longitude_deg}
                            interactive
                            className="absolute inset-0 h-full w-full rounded-r10 border-0"
                        />
                    </div>
                </div>
            </div>
        ) : photoShowNoGps ? (
            <div className="w-full text-center">
                <p className="mb-1 text-h5 font-semibold text-white">
                    {t("gallery.location", { defaultValue: "Location" })}
                </p>
                <p className="text-h5 text-black40">
                    {t("gallery.locationUnavailable", {
                        defaultValue: "No GPS location for this photo.",
                    })}
                </p>
            </div>
        ) : null;

    return (
        <div
            className={twMerge(
                "flex min-h-0 flex-1 flex-col overflow-hidden",
                className
            )}
            onContextMenu={preventGalleryContextMenu}
        >
            <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-black80 px-4 py-3">
                <button
                    type="button"
                    className={filterChipClass(filter === "all")}
                    onClick={() => setFilter("all")}
                >
                    {t("gallery.filterAll", { defaultValue: "All" })}
                </button>
                <button
                    type="button"
                    className={filterChipClass(filter === "photos")}
                    onClick={() => setFilter("photos")}
                >
                    {t("gallery.filterPhotos", { defaultValue: "Photos" })}
                </button>
                <button
                    type="button"
                    className={filterChipClass(filter === "videos")}
                    onClick={() => setFilter("videos")}
                >
                    {t("gallery.filterVideos", { defaultValue: "Videos" })}
                </button>
            </div>

            {bulkSelectActive && (
                <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-black80 bg-black100 px-4 py-2">
                    <label className="flex cursor-pointer items-center gap-2 text-h5">
                        <input
                            ref={selectAllCheckboxRef}
                            type="checkbox"
                            className="h-4 w-4 shrink-0 rounded border-black80 bg-black70"
                            checked={bulkAllSelected}
                            onChange={toggleSelectAll}
                            disabled={bulkActionsPending}
                        />
                        <span>Select all</span>
                    </label>
                    <span className="text-h5 text-black40">{bulkSelectedCount} selected</span>
                    <div className="flex shrink-0 flex-nowrap items-center gap-2">
                        <button
                            type="button"
                            className="inline-flex items-center gap-1.5 rounded-r5 border border-primary2/50 bg-primary2/15 px-3 py-1.5 text-h5 text-primary2 hover:bg-primary2/25 disabled:opacity-50"
                            disabled={bulkSelectedCount === 0 || bulkActionsPending}
                            onClick={() => void handleBatchDownload()}
                        >
                            <DownloadBulkIcon
                                className="h-4 w-4 shrink-0"
                                style={{ width: "1rem", height: "1rem" }}
                                aria-hidden
                            />
                            Download selected
                        </button>
                        <button
                            type="button"
                            className="inline-flex items-center gap-1.5 rounded-r5 border border-red-900/50 bg-red-950/40 px-3 py-1.5 text-h5 text-red-300 hover:bg-red-950/55 disabled:opacity-50"
                            disabled={bulkSelectedCount === 0 || bulkActionsPending}
                            onClick={() => void handleBatchDelete()}
                        >
                            <DeleteBulkIcon
                                className="h-4 w-4 shrink-0"
                                style={{ width: "1rem", height: "1rem" }}
                                aria-hidden
                            />
                            Delete selected
                        </button>
                    </div>
                    <button
                        type="button"
                        className="ml-auto text-h5 text-primary2 hover:text-primary1 disabled:opacity-50"
                        disabled={bulkActionsPending}
                        onClick={exitBulkSelection}
                    >
                        Done
                    </button>
                </div>
            )}

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-1.5 pb-4 pt-2 sm:px-2">
                {loading && <p className="text-h5 p-2 text-black40">Loading…</p>}
                {listError && !loading && (
                    <p className="text-h5 p-2 text-red-400">{listError}</p>
                )}
                {!loading && !listError && data && (
                    <>
                        {filteredFiles.length === 0 ? (
                            <p className="text-h5 p-2 text-black40">{emptyMessage}</p>
                        ) : (
                            <div className="grid grid-cols-5 gap-0.5">
                                {filteredFiles.map((f) => {
                                    const kind = mediaKind(f.name);
                                    if (!kind) return null;
                                    const src = baseUrl
                                        ? getRecordingFileUrl(baseUrl, f.name)
                                        : "";
                                    const selected =
                                        bulkSelectActive && bulkSelected.has(f.name);
                                    return (
                                        <button
                                            key={f.path}
                                            type="button"
                                            className={twMerge(
                                                "relative aspect-square overflow-hidden bg-black100",
                                                selected
                                                    ? "ring-2 ring-inset ring-primary2"
                                                    : "ring-0"
                                            )}
                                            onTouchStart={onCardTouchStart(f.name)}
                                            onTouchMove={onCardTouchMove}
                                            onTouchEnd={onCardTouchEnd}
                                            onTouchCancel={onCardTouchEnd}
                                            onContextMenu={preventGalleryContextMenu}
                                            onClick={() => onCardClick(f.name, kind)}
                                            aria-label={
                                                bulkSelectActive
                                                    ? `Select ${f.name}`
                                                    : kind === "video"
                                                      ? `Play ${f.name}`
                                                      : `Open ${f.name}`
                                            }
                                        >
                                            {bulkSelectActive ? (
                                                <div
                                                    className={twMerge(
                                                        "pointer-events-none absolute left-1.5 top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded border text-[0.65rem] font-semibold leading-none",
                                                        selected
                                                            ? "border-primary2 bg-primary2 text-black"
                                                            : "border-black80 bg-black90/80 text-black60"
                                                    )}
                                                    aria-hidden
                                                >
                                                    {selected ? "✓" : ""}
                                                </div>
                                            ) : null}
                                            {kind === "video" ? (
                                                <>
                                                    {src ? (
                                                        <VideoPreview
                                                            src={src}
                                                            label={f.name}
                                                        />
                                                    ) : null}
                                                    <span
                                                        className="pointer-events-none absolute inset-0 flex items-center justify-center"
                                                        aria-hidden
                                                    >
                                                        <PlayIcon
                                                            className="h-[36%] w-[36%] max-h-10 max-w-10 opacity-50"
                                                            style={{
                                                                width: "36%",
                                                                height: "36%",
                                                                maxWidth: "2.5rem",
                                                                maxHeight: "2.5rem",
                                                            }}
                                                        />
                                                    </span>
                                                </>
                                            ) : src ? (
                                                <img
                                                    src={src}
                                                    alt=""
                                                    draggable={false}
                                                    className="pointer-events-none h-full w-full object-cover"
                                                    onContextMenu={preventGalleryContextMenu}
                                                    style={{ WebkitTouchCallout: "none" }}
                                                />
                                            ) : null}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
            {photoMounted && baseUrl && displayPhotoName ? (
                <Portal visible>
                    <div
                        className={twMerge(
                            "fixed inset-0 z-[200] flex flex-col bg-black",
                            !photoGestures.dragging &&
                                (photoPresence === "exiting"
                                    ? "animate-ios-sheet-out"
                                    : "animate-ios-sheet-in")
                        )}
                        data-dragging={
                            photoGestures.dragging || photoSheetGestures.dragging
                                ? "true"
                                : "false"
                        }
                        style={{
                            ...photoGestures.sheetStyle,
                            ...(photoPresence === "exiting"
                                ? { pointerEvents: "none" as const }
                                : null),
                        }}
                        onContextMenu={preventGalleryContextMenu}
                        role="dialog"
                        aria-modal="true"
                        aria-label={displayPhotoName}
                    >
                        {/* Top chrome over fullscreen photo */}
                        <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex items-center justify-between gap-3 px-3 pt-3">
                            <div className="min-w-0 flex-1 pr-3">
                                <p className="truncate text-h5 font-semibold text-white drop-shadow">
                                    {displayPhotoName}
                                </p>
                            </div>
                            <button
                                type="button"
                                aria-label="Close"
                                className="pointer-events-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-black80/80 text-white backdrop-blur-sm hover:bg-black70"
                                onClick={closePhotoDetail}
                            >
                                <CloseIcon
                                    className="h-4 w-4 [&_path]:fill-current"
                                    style={{ width: "1rem", height: "1rem" }}
                                />
                            </button>
                        </div>

                        {/* Full-bleed photo — tap toggles ajar; swipe up/down drives sheet / dismiss */}
                        <div
                            className="relative flex min-h-0 flex-1 touch-none items-center justify-center"
                            {...photoGestures.bindSwipeTarget}
                            onPointerDown={(e) => {
                                photoSwipeFiredRef.current = false;
                                photoTapStartRef.current = {
                                    x: e.clientX,
                                    y: e.clientY,
                                };
                                (
                                    photoGestures.bindSwipeTarget as {
                                        onPointerDown?: (
                                            ev: React.PointerEvent
                                        ) => void;
                                    }
                                ).onPointerDown?.(e);
                            }}
                            onPointerUp={(e) => {
                                (
                                    photoGestures.bindSwipeTarget as {
                                        onPointerUp?: (
                                            ev: React.PointerEvent
                                        ) => void;
                                    }
                                ).onPointerUp?.(e);
                                const start = photoTapStartRef.current;
                                photoTapStartRef.current = null;
                                if (!start || photoSwipeFiredRef.current) return;
                                const dist = Math.hypot(
                                    e.clientX - start.x,
                                    e.clientY - start.y
                                );
                                if (dist < 12) onPhotoSurfaceClick();
                            }}
                        >
                            <img
                                key={displayPhotoName}
                                src={getRecordingFileUrl(baseUrl, displayPhotoName)}
                                alt={displayPhotoName}
                                draggable={false}
                                className="h-full w-full object-contain"
                                onContextMenu={preventGalleryContextMenu}
                                style={{ WebkitTouchCallout: "none" }}
                            />
                        </div>

                        {/* Closed cue: peek tab (tap or swipe up also works on the photo) */}
                        {photoSheetSnap === "closed" &&
                        closedPeekReady &&
                        !photoSheetGestures.dragging ? (
                            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex justify-center pb-[max(0.5rem,env(safe-area-inset-bottom))]">
                                <button
                                    type="button"
                                    onClick={advancePhotoDetails}
                                    aria-label={t("gallery.showDetails", {
                                        defaultValue: "Show location and details",
                                    })}
                                    className="pointer-events-auto flex w-28 flex-col items-center rounded-t-r10 border border-b-0 border-black80 bg-black100 px-4 pb-2 pt-2 transition-colors hover:bg-black90"
                                >
                                    <div
                                        className="mb-1 h-1 w-10 rounded-full bg-black40"
                                        aria-hidden
                                    />
                                    <span className="text-h5 text-black40">
                                        {t("gallery.detailsHandle", {
                                            defaultValue: "Details",
                                        })}
                                    </span>
                                </button>
                            </div>
                        ) : null}

                        {/* iOS-style details sheet over the photo */}
                        <div
                            className={twMerge(
                                "absolute inset-x-0 bottom-0 z-30 flex flex-col overflow-hidden rounded-t-[1.25rem] border-t border-black80 bg-black100 shadow-[0_-8px_40px_rgba(0,0,0,0.55)]",
                                photoSheetSnap === "closed" &&
                                    !photoSheetGestures.dragging &&
                                    "pointer-events-none border-0 shadow-none"
                            )}
                            style={photoSheetGestures.sheetStyle}
                            aria-hidden={photoSheetSnap === "closed"}
                            {...photoSheetGestures.bindSheetDrag}
                        >
                            <div
                                data-sheet-handle
                                className="flex shrink-0 touch-none cursor-grab flex-col items-center pt-2 pb-1 active:cursor-grabbing"
                            >
                                <div
                                    className="h-1 w-10 rounded-full bg-black40"
                                    aria-hidden
                                />
                                <button
                                    type="button"
                                    className="mt-1 px-4 py-1 text-h5 text-black40"
                                    aria-label={t("gallery.showDetails", {
                                        defaultValue: "Show location and details",
                                    })}
                                    onClick={advancePhotoDetails}
                                >
                                    {t("gallery.detailsHandle", {
                                        defaultValue: "Details",
                                    })}
                                </button>
                            </div>

                            <div
                                ref={photoSheetScrollRef}
                                className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4"
                                style={{ touchAction: "pan-y" }}
                            >
                                {showPhotoExpandedBody ? (
                                    <div className="flex min-h-full flex-col items-center gap-3 pb-2">
                                        {photoMetaBlock}
                                        {photoMapBlock}
                                    </div>
                                ) : (
                                    <div className="pb-2">
                                        <div className="mb-3">{photoMetaBlock}</div>
                                        <p className="text-center text-h5 text-black60">
                                            {t("gallery.dragForMore", {
                                                defaultValue: "Drag up for map and more",
                                            })}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {showPhotoExpandedBody ? (
                                <div className="flex shrink-0 justify-center border-t border-black80 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                                    {renderMediaActions(
                                        displayPhotoName,
                                        viewingFile,
                                        "photo",
                                        { className: "justify-center" }
                                    )}
                                </div>
                            ) : (
                                <div className="shrink-0 pb-[max(0.5rem,env(safe-area-inset-bottom))]" />
                            )}
                        </div>
                    </div>
                </Portal>
            ) : null}

            {videoMounted && baseUrl && displayVideoName ? (
                <Portal visible>
                    <div
                        className={twMerge(
                            "fixed inset-0 z-[200] flex flex-col bg-black",
                            !videoSwipe.dragging &&
                                (videoPresence === "exiting"
                                    ? "animate-ios-sheet-out"
                                    : "animate-ios-sheet-in")
                        )}
                        data-dragging={videoSwipe.dragging ? "true" : "false"}
                        style={{
                            ...videoSwipe.sheetStyle,
                            ...(videoPresence === "exiting"
                                ? { pointerEvents: "none" as const }
                                : null),
                        }}
                        onContextMenu={preventGalleryContextMenu}
                        role="dialog"
                        aria-modal="true"
                        aria-label={displayVideoName}
                    >
                        <div className="flex shrink-0 flex-col">
                            <div
                                className="flex touch-none cursor-grab items-center justify-center pt-2 pb-1 active:cursor-grabbing"
                                {...videoSwipe.bindSwipeTarget}
                            >
                                <div className="h-1 w-10 rounded-full bg-black60" aria-hidden />
                            </div>
                            <div className="flex items-center justify-between px-4 pb-3">
                                <p className="text-h5 min-w-0 flex-1 truncate pr-3 font-semibold text-white">
                                    {displayVideoName}
                                </p>
                                <button
                                    type="button"
                                    aria-label="Close"
                                    className="relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-r5 text-black40 hover:bg-black80 hover:text-white"
                                    onClick={closeVideoDetail}
                                >
                                    <ReturnIcon
                                        className="h-5 w-5 [&_path]:fill-current"
                                        style={{ width: "1.25rem", height: "1.25rem" }}
                                    />
                                </button>
                            </div>
                        </div>
                        <div
                            className="relative flex min-h-0 flex-1 touch-none items-center justify-center px-2"
                            {...videoSwipe.bindSwipeTarget}
                        >
                            <video
                                key={displayVideoName}
                                className="max-h-full max-w-full bg-black object-contain"
                                style={{ WebkitTouchCallout: "none" }}
                                src={getRecordingFileUrl(baseUrl, displayVideoName)}
                                controls
                                playsInline
                                preload="metadata"
                                autoPlay
                                draggable={false}
                                onContextMenu={preventGalleryContextMenu}
                                onPointerDown={(e) => e.stopPropagation()}
                            >
                                Playback is not supported in this browser.
                            </video>
                        </div>
                        <div className="max-h-[40vh] overflow-y-auto">
                            {renderDetailInfo(
                                displayVideoName,
                                playingFile,
                                playingMeta,
                                playingMetaReady,
                                "video"
                            )}
                        </div>
                    </div>
                </Portal>
            ) : null}

            {mediaEdit && baseUrl ? (
                <Portal visible>
                    <div
                        className="fixed inset-0 z-[210] flex items-center justify-center bg-black/65 p-4"
                        onClick={() => !editSaving && closeMediaEdit()}
                        role="presentation"
                    >
                        <div
                            className="relative w-full max-w-md rounded-r10 border border-black80 bg-black90 p-4 shadow-lg"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <p className="text-h4 mb-3 font-semibold text-white">
                                {mediaEdit.kind === "video" ? "Edit video" : "Edit photo"}
                            </p>
                            <label className="mb-1 block text-h5 text-black40">File name</label>
                            <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                className="mb-3 w-full rounded-r5 border border-black80 bg-black70 px-3 py-2 text-h5 text-white focus:border-primary2 focus:outline-none"
                                disabled={editSaving}
                                autoComplete="off"
                            />
                            <label className="mb-1 block text-h5 text-black40">Remark</label>
                            <textarea
                                value={editRemark}
                                onChange={(e) => setEditRemark(e.target.value)}
                                rows={4}
                                maxLength={8000}
                                className="mb-3 w-full resize-y rounded-r5 border border-black80 bg-black70 px-3 py-2 text-h5 text-white focus:border-primary2 focus:outline-none"
                                disabled={editSaving}
                                placeholder="Notes about this file…"
                            />
                            <p className="text-h5 mb-3 text-right text-black60">
                                {editRemark.length}/8000
                            </p>
                            {editGps && formatGpsLine(editGps) ? (
                                <div className="mb-3">
                                    <p className="mb-1 text-h5 text-black40">
                                        {t("gallery.location", { defaultValue: "Location" })}
                                    </p>
                                    <p className="text-h5 text-white">{formatGpsLine(editGps)}</p>
                                </div>
                            ) : null}
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    className="rounded-r5 bg-primary2 px-4 py-2 text-h5 font-semibold text-black hover:bg-primary1 disabled:opacity-50"
                                    disabled={editSaving || bulkActionsPending}
                                    onClick={() => void saveMediaEdit()}
                                >
                                    Save
                                </button>
                                <button
                                    type="button"
                                    className="rounded-r5 border border-black80 bg-black80 px-4 py-2 text-h5 text-white hover:bg-black70 disabled:opacity-50"
                                    disabled={editSaving || bulkActionsPending}
                                    onClick={closeMediaEdit}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="ml-auto inline-flex items-center gap-1.5 rounded-r5 border border-red-900/50 bg-red-950/40 px-3 py-2 text-h5 text-red-300 hover:bg-red-950/55 disabled:opacity-50"
                                    disabled={editSaving || bulkActionsPending}
                                    onClick={() => void handleDeleteOne(editTargetFile)}
                                >
                                    <DeleteBulkIcon
                                        className="h-4 w-4 shrink-0"
                                        style={{ width: "1rem", height: "1rem" }}
                                        aria-hidden
                                    />
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </Portal>
            ) : null}

        </div>
    );
};

export default RecordingsGallery;
